import { useState, useEffect, useRef, useCallback } from "react";
import { useEditor, createDoc } from "../store/editor.js";
import {
  devLogin,
  listMaps,
  getMap,
  createMap,
  updateMap,
  type MapSummary,
} from "../api/client.js";

const AUTOSAVE_DEBOUNCE_MS = 2000;

export function SaveBar(): React.ReactElement {
  const { doc, revision, dirty, setDoc, markSaved } = useEditor();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);

  const save = useCallback(
    async (currentDoc: typeof doc, currentRev: number) => {
      if (savingRef.current) return;
      savingRef.current = true;
      try {
        if (currentRev === 0) {
          const res = await createMap(currentDoc);
          setDoc({ ...currentDoc, revision: res.revision }, res.revision);
        } else {
          const res = await updateMap(currentDoc.id, currentDoc);
          setDoc({ ...currentDoc, revision: res.revision }, res.revision);
        }
        markSaved();
      } catch (err) {
        console.error("save failed", err);
      } finally {
        savingRef.current = false;
      }
    },
    [setDoc, markSaved],
  );

  useEffect(() => {
    if (!dirty) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void save(doc, revision);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [dirty, doc, revision, save]);

  const status = dirty ? "Unsaved changes..." : "Saved";

  return (
    <div style={saveBarStyle}>
      <span style={statusStyle}>{status}</span>
      <span style={revStyle}>rev {revision}</span>
    </div>
  );
}

export function StartScreen(): React.ReactElement {
  const { setDoc } = useEditor();
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        await devLogin();
        const list = await listMaps();
        setMaps(list);
      } catch (err) {
        console.error("failed to load maps", err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const handleNew = () => {
    setDoc(createDoc("Untitled Map"), 0);
    setOpened(true);
  };

  const handleOpen = (id: string) => {
    void (async () => {
      const { document, map } = await getMap(id);
      setDoc(document, map.revision);
      setOpened(true);
    })();
  };

  if (opened) return <></>;

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <h1 style={h1Style}>Maps</h1>
        <button onClick={handleNew} style={newBtnStyle}>New Map</button>
        <div style={mapListStyle}>
          {loaded
            ? maps.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleOpen(m.id)}
                  style={mapItemStyle}
                >
                  <span style={mapNameStyle}>{m.name}</span>
                  <span style={mapMetaStyle}>
                    rev {m.revision} {m.isPublished ? "published" : "draft"}
                  </span>
                </button>
              ))
            : <span style={loadingStyle}>Loading...</span>}
          {loaded && maps.length === 0 && (
            <span style={loadingStyle}>No maps yet — create one to get started.</span>
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "#121218",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
};

const cardStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 32,
  maxWidth: 480,
  width: "100%",
};

const h1Style: React.CSSProperties = {
  fontSize: 28,
  margin: 0,
  color: "#e0e0f0",
};

const newBtnStyle: React.CSSProperties = {
  background: "#4466ff",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "10px 16px",
  fontSize: 15,
  cursor: "pointer",
};

const mapListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const mapItemStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#2a2a38",
  border: "1px solid #383850",
  borderRadius: 6,
  padding: "12px 16px",
  cursor: "pointer",
  color: "#cccce0",
};

const mapNameStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
};

const mapMetaStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#666680",
};

const loadingStyle: React.CSSProperties = {
  color: "#666680",
  fontSize: 14,
  padding: 16,
  textAlign: "center",
};

const saveBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "4px 12px",
  background: "#121218",
  borderTop: "1px solid #2a2a35",
  height: 28,
  flexShrink: 0,
  fontSize: 12,
  color: "#666680",
  userSelect: "none",
};

const statusStyle: React.CSSProperties = {
  color: "#8888aa",
};

const revStyle: React.CSSProperties = {
  marginLeft: "auto",
  color: "#555570",
};